/**
 * Dispatch toolbar filters: empty-project visibility, weekends, project, scope
 * name, and worker. The multi-select filters combine (AND) and cross-curate
 * each other's options. Thin wrapper mapping Dispatch state onto the shared
 * {@link FiltersPanel}.
 */
import { useCallback, useContext } from "react";

import { PLANNING_TOOLTIP_MESSAGES } from "../../../config";
import { FiltersPanel } from "../../../components/ui";
import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { WeeklyContext } from "../../../context/global/WeeklyContext";

/**
 * Filters button and popover; reads filter state from {@link DispatchContext}
 * and the Show Weekends toggle from {@link WeeklyContext}.
 *
 * @returns {JSX.Element}
 */
export default function DispatchFiltersPanel() {
  const { showWeekends, setShowWeekends } = useContext(WeeklyContext);
  const {
    hideEmptyProjects,
    setHideEmptyProjects,
    filterProjectId,
    setFilterProjectId,
    filterScopeId,
    setFilterScopeId,
    filterWorkerId,
    setFilterWorkerId,
    dispatchFilterProjectOptions,
    dispatchFilterScopeOptions,
    dispatchFilterWorkerOptions,
    dispatchFilterActive,
    clearDispatchFilters,
  } = useContext(DispatchContext);

  // Show Weekends lives in WeeklyContext, so it is composed into the
  // active state and clear-all behavior here rather than in the hook.
  const filtersActive = dispatchFilterActive || showWeekends;

  const clearAllFilters = useCallback(() => {
    setShowWeekends(false);
    clearDispatchFilters();
  }, [setShowWeekends, clearDispatchFilters]);

  return (
    <FiltersPanel
      ariaLabel="Dispatch filters"
      iconOnly
      isActive={filtersActive}
      onClearAll={clearAllFilters}
      checkboxes={[
        {
          id: "show-empty-projects",
          label: "Show Empty Projects",
          title: PLANNING_TOOLTIP_MESSAGES.showEmptyProjectsDispatch,
          checked: !hideEmptyProjects,
          onChange: (checked) => setHideEmptyProjects(!checked),
        },
        {
          id: "show-weekends",
          label: "Show Weekends",
          checked: showWeekends,
          onChange: setShowWeekends,
        },
      ]}
      selects={[
        {
          id: "project",
          label: "Project",
          value: filterProjectId,
          options: dispatchFilterProjectOptions,
          onChange: setFilterProjectId,
          placeholder: "Search projects...",
          emptyPlaceholder: "Search projects...",
        },
        {
          id: "scope-name",
          label: "Scope name",
          value: filterScopeId,
          options: dispatchFilterScopeOptions,
          onChange: setFilterScopeId,
          placeholder: "Search scopes...",
          emptyPlaceholder: "Search scopes...",
        },
        {
          id: "worker",
          label: "Worker",
          value: filterWorkerId,
          options: dispatchFilterWorkerOptions,
          onChange: setFilterWorkerId,
          placeholder: "Search workers...",
          emptyPlaceholder: "Search workers...",
        },
      ]}
    />
  );
}
